import { Grid } from '@material-ui/core';
import {
  DefaultCard,
  DisplayItem,
  FormHookResult,
  FormValueTypes,
} from 'design-system';
import React from 'react';
import {
  makeSearchableDropDown,
  makeTextField,
} from 'src/components/MakeFormFields';

import { COSTA_RICA } from 'src/constants/countryCode';
import { cities } from 'src/data/citiesCr.json';
import { states } from 'src/data/statesCr.json';
import { townships } from 'src/data/townshipsCr.json';
import { getCatalogToOptions } from 'src/functions/equifax';
import { isAlphaNumericForAddress } from 'src/functions/isAlphaNumeric';
import { LoadingEquifax } from '../NaturalPersonData/components/LoadingEquifax';

type UpdateMultipleInputType = (newInputdata: {
  [key: string]: FormValueTypes;
}) => void;

type AddressDataProps = {
  getInputProps: FormHookResult;
  updateMultipleInputs: UpdateMultipleInputType;
  countryOptions: DisplayItem[];
  requestingCountries: boolean;
  stateOptions: DisplayItem[];
  requestingStates: boolean;
  cityOptions: DisplayItem[];
  townshipOptions: DisplayItem[];
  cardTitle: string;
  detailPlaceholder?: string;
  personIsInvalid: boolean;
  detailLabel: string;
  isLoading?: boolean;
  setCityOptions: (options: DisplayItem[]) => void;
  setTownshipOptions: (options: DisplayItem[]) => void;
  fetchCities?: (state: string) => void;
  fetchTownships?: (city: string) => void;
};

export function makeAddressDataSection({
  getInputProps,
  updateMultipleInputs,
  countryOptions,
  requestingCountries,
  stateOptions,
  requestingStates,
  cityOptions,
  townshipOptions,
  cardTitle,
  detailPlaceholder,
  detailLabel,
  personIsInvalid,
  isLoading,
  setCityOptions,
  setTownshipOptions,
  fetchCities,
  fetchTownships,
}: AddressDataProps): React.ReactNode {
  const addressCountry = getInputProps('addressCountry').value as string;
  const isNotFromCostaRica = addressCountry !== COSTA_RICA;

  const onChangeCountry = (): void => {
    updateMultipleInputs({
      addressState: '',
      addressCity: '',
      addressTownship: '',
    });
  };

  const onChangeState = (newVal: string): void => {
    updateMultipleInputs({
      addressCity: '',
      addressTownship: '',
    });
    const state = states.find((item) => item.code === newVal);
    setCityOptions(getCatalogToOptions(cities, state?.id));
    setTownshipOptions([]);
    fetchCities?.(newVal);
  };

  const onChangeCity = (newVal: string): void => {
    updateMultipleInputs({
      addressTownship: '',
    });
    const stateInput = getInputProps('addressState');
    const stateValue = stateInput.value as string;
    const state = states.find((item) => item.code === stateValue);
    const citiesFilter = cities.filter((item) => item.parentId === state?.id);
    const city = citiesFilter.find((item) => item.code === newVal);
    setTownshipOptions(getCatalogToOptions(townships, city?.id));
    fetchTownships?.(newVal);
  };

  return isLoading ? (
    <Grid item xs={12}>
      <LoadingEquifax />
    </Grid>
  ) : (
    <Grid item xs={12}>
      <DefaultCard data-testid="address-data-card" title={cardTitle}>
        <br />
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            {makeSearchableDropDown({
              inputId: 'swi-address-country',
              label: 'País',
              getInputProps,
              inputName: 'addressCountry',
              dropdownList: countryOptions,
              requesting: requestingCountries,
              onUpdate: onChangeCountry,
              isDisabled: personIsInvalid,
            })}
          </Grid>

          <Grid item xs={12} md={6}>
            {makeSearchableDropDown({
              inputId: 'swi-address-state',
              label: 'Provincia',
              getInputProps,
              inputName: 'addressState',
              dropdownList: stateOptions,
              requesting: requestingStates,
              isDisabled: isNotFromCostaRica || personIsInvalid,
              onUpdate: onChangeState,
            })}
          </Grid>

          <Grid item xs={12} md={6}>
            {makeSearchableDropDown({
              inputId: 'swi-address-city',
              label: 'Cantón',
              getInputProps,
              inputName: 'addressCity',
              dropdownList: cityOptions,
              isDisabled: isNotFromCostaRica || personIsInvalid,
              requesting: false,
              onUpdate: onChangeCity,
            })}
          </Grid>

          <Grid item xs={12} md={6}>
            {makeSearchableDropDown({
              inputId: 'swi-address-township',
              label: 'Distrito',
              getInputProps,
              inputName: 'addressTownship',
              dropdownList: townshipOptions,
              requesting: false,
              isDisabled: isNotFromCostaRica || personIsInvalid,
            })}
          </Grid>

          <Grid item xs={12}>
            {makeTextField({
              inputId: 'swi-address-detail',
              label: detailLabel,
              placeholder: detailPlaceholder,
              helperText: 'La dirección no debe superar los 250 caracteres',
              inputName: 'addressDetail',
              getInputProps,
              isDisabled: personIsInvalid,
              shouldUpdate: isAlphaNumericForAddress,
            })}
          </Grid>
        </Grid>
      </DefaultCard>
    </Grid>
  );
}

export default makeAddressDataSection;
